package dev.akshaychordiya.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.akshaychordiya.HeadlineTextStyle
import dev.akshaychordiya.SectionTitleStyle
import dev.akshaychordiya.SubheadlineTextStyle
import dev.akshaychordiya.fadeIn
import dev.akshaychordiya.fadeUp
import dev.akshaychordiya.smoothTransition
import dev.akshaychordiya.toSitePalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

/**
 * The opening block shared by every content page: a small eyebrow label, a headline, and a supporting line.
 *
 * The three lines arrive in sequence rather than together, which gives the page a sense of settling into place.
 */
@Composable
fun PageHeader(eyebrow: String, title: String, subtitle: String = "") {
    Column(Modifier.fillMaxWidth().gap(0.6.cssRem).margin(top = 1.5.cssRem, bottom = 2.5.cssRem)) {
        Div(SectionTitleStyle.toModifier().fadeIn(index = 0).toAttrs()) { SpanText(eyebrow) }
        Div(HeadlineTextStyle.toModifier().fadeUp(index = 1).toAttrs()) { SpanText(title) }
        if (subtitle.isNotEmpty()) {
            Div(SubheadlineTextStyle.toModifier().fadeUp(index = 2).toAttrs()) { SpanText(subtitle) }
        }
    }
}

/**
 * A list row separated by a hairline rather than boxed in a card - quieter, and it scales to long lists.
 *
 * Hovering warms the background very slightly and nudges the row right. It's deliberately subtle: the cue should
 * register without the page feeling like it is moving under the cursor.
 */
val ListRowStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 1.5.cssRem, leftRight = 0.75.cssRem)
            .margin(leftRight = (-0.75).cssRem)
            .borderRadius(0.6.cssRem)
            .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .smoothTransition("background-color", "transform")
    }
    hover {
        Modifier
            .backgroundColor(colorMode.toSitePalette().surface)
            .translateX(4.px)
    }
}

/** A raised surface, for content that should read as a distinct object (a book, a bookable session). */
val CardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(1.5.cssRem)
            .borderRadius(0.9.cssRem)
            .backgroundColor(colorMode.toSitePalette().surface)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .smoothTransition("transform", "border-color", "box-shadow")
    }
    hover {
        Modifier
            .translateY((-3).px)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().brandPrimary.toRgb().copyf(alpha = 0.45f))
            .boxShadow(
                offsetY = 8.px,
                blurRadius = 24.px,
                color = colorMode.toSitePalette().brandPrimary.toRgb().copyf(alpha = 0.10f),
            )
    }
}
